import type { ReactNode } from "react";
import { useLocale } from "@genius-tracker/i18n/react";

import {
  useSeriesAlertsWeekly,
  useSeriesBackupDuration,
  useSeriesDiskGrowth,
  useSeriesLatency,
  useSeriesTelemetryIngress,
  useSeriesUptimeWeekly,
} from "../../../api/series.js";
import type { GtccMeasured, GtccResult } from "../../../api/gtcc-result.js";
import { ChartPanel, TriGrid } from "../../../ui/primitives.js";

/**
 * Y-Achse eines Sparklines: `floor` ist der Wert am Boden (82), `ceiling` der
 * Wert an der Spitze (18). Nullbasiert ist `floor === 0`.
 */
function scaleY(value: number, floor: number, ceiling: number): number {
  const span = ceiling - floor;
  if (span <= 0) return 82;
  return 82 - ((value - floor) / span) * 64;
}

function linePoints(values: readonly number[], floor: number, ceiling: number): string {
  return values
    .map((value, index) => {
      const x = values.length === 1 ? 150 : (index * 300) / (values.length - 1);
      return `${x},${scaleY(value, floor, ceiling)}`;
    })
    .join(" ");
}

/**
 * `trendText` ist serverseitig ein Maschinen-Token
 * (`services/api/src/modules/gtcc/series/service.ts`: "increasing",
 * "decreasing", "insufficient history", "not measurable") und kein Anzeigetext.
 * Unbekannte Token werden weggelassen statt rohen englischen Text zu zeigen.
 */
function trendClause(t: ReturnType<typeof useLocale>["t"], trendText: string): string | null {
  if (trendText === "decreasing") return t("admin.gtcc.cockpit.trends.alerts.trend_falling");
  if (trendText === "increasing") return t("admin.gtcc.cockpit.trends.alerts.trend_rising");
  if (trendText === "insufficient history")
    return t("admin.gtcc.cockpit.trends.alerts.trend_unknown");
  return null;
}

function unavailable<T extends GtccMeasured>(
  result: GtccResult<T> | undefined,
  loading: string,
  unavailableLabel: string,
  reason?: string,
): ReactNode | null {
  if (!result) return <div className="sub">{loading}</div>;
  if (result.ok && result.data.status !== "unmeasurable") return null;
  const detail = result.ok ? reason : result.kind;
  return (
    <div className="sub">
      {unavailableLabel}
      {detail ? `: ${detail}` : ""}
    </div>
  );
}

function TrendSvg({
  label,
  values,
  colour = "var(--gt-sys-accent)",
  threshold,
  bars = false,
  zoomToRange = false,
}: {
  readonly label: string;
  readonly values: readonly number[];
  readonly colour?: string;
  readonly threshold?: { readonly value: number; readonly label: string } | undefined;
  readonly bars?: boolean;
  /**
   * Skaliert auf den gemessenen Wertebereich statt auf Null. Nötig für Reihen,
   * die sich dicht unter ihrer Decke bewegen (Uptime 99,7–100 % gegen eine
   * SLO-Linie bei 99,9 %): nullbasiert liegen Datenlinie und Schwellenlinie
   * beide auf ~y=18 und die Karte sieht leer aus.
   */
  readonly zoomToRange?: boolean;
}): ReactNode {
  const ceiling = Math.max(threshold?.value ?? 0, ...values, 1);
  let floor = 0;
  if (zoomToRange && values.length > 0) {
    const low = Math.min(threshold?.value ?? ceiling, ...values);
    const span = ceiling - low;
    floor = low - (span > 0 ? span * 0.4 : Math.max(ceiling * 0.001, 0.05));
  }
  const thresholdY = threshold ? scaleY(threshold.value, floor, ceiling) : undefined;
  return (
    <svg aria-label={label} viewBox="0 0 300 90" preserveAspectRatio="none" className="spark">
      {threshold && thresholdY !== undefined ? (
        <>
          <line
            x1="0"
            y1={thresholdY}
            x2="300"
            y2={thresholdY}
            stroke="var(--gt-sys-warning, #a16207)"
            strokeDasharray="4 3"
          />
          <text
            x="295"
            y={thresholdY - 3}
            textAnchor="end"
            fontSize="8"
            fill="var(--gt-sys-warning, #a16207)"
          >
            {threshold.label}
          </text>
        </>
      ) : (
        <line x1="0" y1="45" x2="300" y2="45" stroke="var(--gt-sys-border)" />
      )}
      {bars ? (
        values.map((value, index) => {
          const height = 82 - scaleY(value, floor, ceiling);
          const width = Math.max(8, 240 / values.length);
          return (
            <rect
              key={index}
              x={10 + index * (280 / values.length)}
              y={82 - height}
              width={width}
              height={height}
              fill={colour}
            />
          );
        })
      ) : (
        <polyline
          points={linePoints(values, floor, ceiling)}
          fill="none"
          stroke={colour}
          strokeWidth="2"
        />
      )}
    </svg>
  );
}

function TrendPanel({
  title,
  range,
  result,
  loading,
  unavailableLabel,
  reason,
  children,
}: {
  readonly title: string;
  readonly range: string;
  readonly result: GtccResult<GtccMeasured> | undefined;
  readonly loading: string;
  readonly unavailableLabel: string;
  readonly reason?: string | undefined;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <ChartPanel title={title} badge={<span className="mono muted">{range}</span>}>
      {unavailable(result, loading, unavailableLabel, reason) ?? children}
    </ChartPanel>
  );
}

export function TrendsTab(): ReactNode {
  const { t } = useLocale();
  const alerts = useSeriesAlertsWeekly();
  const disk = useSeriesDiskGrowth();
  const uptime = useSeriesUptimeWeekly();
  const backup = useSeriesBackupDuration();
  const latency = useSeriesLatency();
  const telemetry = useSeriesTelemetryIngress();
  const alertsData = alerts.data?.ok ? alerts.data.data : undefined;
  const diskData = disk.data?.ok ? disk.data.data : undefined;
  const uptimeData = uptime.data?.ok ? uptime.data.data : undefined;
  const backupData = backup.data?.ok ? backup.data.data : undefined;
  const backupValues =
    backupData?.points?.flatMap((point) => (point.seconds === null ? [] : [point.seconds])) ?? [];

  return (
    <div data-testid="gtcc-cockpit-trends-tab">
      <TriGrid>
        <TrendPanel
          title={t("admin.gtcc.cockpit.trends.alerts.title")}
          range={t("admin.gtcc.cockpit.trends.alerts.range")}
          result={alerts.data}
          loading={t("admin.gtcc.cockpit.trends.loading")}
          unavailableLabel={t("admin.gtcc.cockpit.trends.not_measured")}
          reason={alertsData?.reason}
        >
          <TrendSvg
            label={t("admin.gtcc.cockpit.trends.alerts.title")}
            bars
            values={alertsData?.points?.map((point) => point.count) ?? []}
          />
          <div className="sub">
            {t("admin.gtcc.cockpit.trends.alerts.subtitle")}{" "}
            {alertsData === undefined ? null : trendClause(t, alertsData.trendText)}
          </div>
        </TrendPanel>
        <TrendPanel
          title={t("admin.gtcc.cockpit.trends.disk.title")}
          range={t("admin.gtcc.cockpit.trends.disk.range")}
          result={disk.data}
          loading={t("admin.gtcc.cockpit.trends.loading")}
          unavailableLabel={t("admin.gtcc.cockpit.trends.not_measured")}
          reason={diskData?.reason}
        >
          <TrendSvg
            label={t("admin.gtcc.cockpit.trends.disk.title")}
            values={[
              ...(diskData?.points?.map((point) => point.usedPercent) ?? []),
              ...(diskData?.forecast?.slice(1).map((point) => point.usedPercent) ?? []),
            ]}
            threshold={{ value: 100, label: "100 %" }}
          />
          <div className="sub">{t("admin.gtcc.cockpit.trends.disk.subtitle")}</div>
          <div className="sub">
            {diskData?.daysToFull === null
              ? t("admin.gtcc.cockpit.trends.disk.days_to_full_unmeasured")
              : diskData?.daysToFull === undefined
                ? null
                : t("admin.gtcc.cockpit.trends.disk.days_to_full", {
                    days: diskData.daysToFull,
                  })}
          </div>
        </TrendPanel>
        <TrendPanel
          title={t("admin.gtcc.cockpit.trends.uptime.title")}
          range={t("admin.gtcc.cockpit.trends.uptime.range")}
          result={uptime.data}
          loading={t("admin.gtcc.cockpit.trends.loading")}
          unavailableLabel={t("admin.gtcc.cockpit.trends.not_measured")}
          reason={uptimeData?.reason}
        >
          <TrendSvg
            label={t("admin.gtcc.cockpit.trends.uptime.title")}
            colour="var(--gt-sys-success)"
            zoomToRange
            values={
              uptimeData?.points?.flatMap((point) =>
                point.percent === null ? [] : [point.percent],
              ) ?? []
            }
            threshold={
              uptimeData
                ? {
                    value: uptimeData.sloPercent,
                    label: `SLO ${uptimeData.sloPercent.toLocaleString("de-DE")} %`,
                  }
                : undefined
            }
          />
          <div className="sub">{t("admin.gtcc.cockpit.trends.uptime.subtitle")}</div>
        </TrendPanel>
        <TrendPanel
          title={t("admin.gtcc.cockpit.trends.latency.title")}
          range={t("admin.gtcc.cockpit.trends.latency.range")}
          result={latency.data}
          loading={t("admin.gtcc.cockpit.trends.loading")}
          unavailableLabel={t("admin.gtcc.cockpit.trends.not_measured")}
          reason={latency.data?.ok ? latency.data.data.reason : undefined}
        >
          <TrendSvg
            label={t("admin.gtcc.cockpit.trends.latency.title")}
            values={
              latency.data?.ok
                ? (latency.data.data.series?.flatMap(
                    (series) =>
                      series.points?.flatMap((point) => (point.v === null ? [] : [point.v])) ?? [],
                  ) ?? [])
                : []
            }
          />
          <div className="sub">{t("admin.gtcc.cockpit.trends.latency.subtitle")}</div>
        </TrendPanel>
        <TrendPanel
          title={t("admin.gtcc.cockpit.trends.telemetry.title")}
          range={t("admin.gtcc.cockpit.trends.telemetry.range")}
          result={telemetry.data}
          loading={t("admin.gtcc.cockpit.trends.loading")}
          unavailableLabel={t("admin.gtcc.cockpit.trends.not_measured")}
          reason={telemetry.data?.ok ? telemetry.data.data.reason : undefined}
        >
          <TrendSvg
            label={t("admin.gtcc.cockpit.trends.telemetry.title")}
            colour="var(--gt-sys-accent)"
            values={
              telemetry.data?.ok
                ? (telemetry.data.data.points?.flatMap((point) =>
                    point.v === null ? [] : [point.v],
                  ) ?? [])
                : []
            }
          />
          <div className="sub">{t("admin.gtcc.cockpit.trends.telemetry.subtitle")}</div>
        </TrendPanel>
        <TrendPanel
          title={t("admin.gtcc.cockpit.trends.backup.title")}
          range={t("admin.gtcc.cockpit.trends.backup.range")}
          result={backup.data}
          loading={t("admin.gtcc.cockpit.trends.loading")}
          unavailableLabel={t("admin.gtcc.cockpit.trends.not_measured")}
          reason={backupData?.reason}
        >
          <TrendSvg
            label={t("admin.gtcc.cockpit.trends.backup.title")}
            values={backupValues}
            threshold={
              backupData?.windowLimitSeconds === null ||
              backupData?.windowLimitSeconds === undefined
                ? undefined
                : {
                    value: backupData.windowLimitSeconds,
                    label: t("admin.gtcc.cockpit.trends.backup.window_limit"),
                  }
            }
          />
          <div className="sub">{t("admin.gtcc.cockpit.trends.backup.subtitle")}</div>
          <div className="sub">
            {backupData?.windowLimitSeconds === null
              ? t("admin.gtcc.cockpit.trends.backup.window_limit_unmeasured")
              : backupData?.windowLimitSeconds === undefined
                ? undefined
                : t("admin.gtcc.cockpit.trends.backup.window_limit_hours", {
                    hours: Math.round(backupData.windowLimitSeconds / 3600),
                  })}
          </div>
        </TrendPanel>
      </TriGrid>
    </div>
  );
}
